import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import { History, ArrowRight } from "lucide-react";
import { formatCurrency } from "@/lib/currencyUtils";

interface QuotePaymentCorrectionHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quotationId: string;
  quoteNumber: string;
  currency: string;
}

interface Correction {
  id: string;
  corrected_by: string;
  correction_reason: string;
  old_payment_status: string | null;
  new_payment_status: string | null;
  old_payment_amount: number | null;
  new_payment_amount: number | null;
  old_payment_reference: string | null;
  new_payment_reference: string | null;
  created_at: string;
  profiles: {
    full_name: string | null;
    email: string | null;
  } | null;
}

export const QuotePaymentCorrectionHistory = ({
  open,
  onOpenChange,
  quotationId,
  quoteNumber,
  currency,
}: QuotePaymentCorrectionHistoryProps) => {
  const { data: corrections, isLoading } = useQuery({
    queryKey: ["quote-payment-corrections", quotationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_payment_corrections")
        .select("*")
        .eq("quotation_id", quotationId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch profile names for correctors
      const userIds = [...new Set((data || []).map(c => c.corrected_by))];
      const { data: profiles } = userIds.length > 0
        ? await supabase.from("profiles").select("id, full_name, email").in("id", userIds)
        : { data: [] };

      const profileMap = new Map((profiles || []).map(p => [p.id, p]));

      return (data || []).map(c => ({
        ...c,
        profiles: profileMap.get(c.corrected_by) || null,
      })) as Correction[];
    },
    enabled: open,
  });

  const formatChange = (label: string, oldVal: any, newVal: any, isAmount = false) => {
    if (oldVal === newVal) return null;

    const formatValue = (val: any) => {
      if (val === null || val === undefined) return "—";
      if (isAmount) return formatCurrency(val, currency);
      return val.toString();
    };

    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium text-muted-foreground">{label}:</span>
        <span className="text-destructive line-through">{formatValue(oldVal)}</span>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <span className="text-green-600">{formatValue(newVal)}</span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Payment Correction History
          </DialogTitle>
          <DialogDescription>
            Audit trail for {quoteNumber}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading history...
            </div>
          ) : corrections && corrections.length > 0 ? (
            <div className="space-y-4">
              {corrections.map((correction) => (
                <div
                  key={correction.id}
                  className="border rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {correction.profiles?.full_name || correction.profiles?.email || "Unknown user"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(correction.created_at), "MMM dd, yyyy 'at' h:mm a")}
                    </span>
                  </div>

                  <div className="space-y-1">
                    {formatChange("Amount", correction.old_payment_amount, correction.new_payment_amount, true)}
                    {formatChange("Status", correction.old_payment_status, correction.new_payment_status)}
                    {formatChange("Reference", correction.old_payment_reference, correction.new_payment_reference)}
                  </div>

                  <div className="bg-muted/50 rounded p-2">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium">Reason:</span> {correction.correction_reason}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No corrections have been made to this quote's payment.
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
